const content = `<p>Factory method is a design pattern that applies <a href="/template-method">Template method</a> to a specific case, where the role of the customization point is to create an object of a certain type.</p>

<p>It’s not to be confused with the other patterns that use the term “factory”: the Abstract factory design pattern, and what people tend to just call <a href="/factory">Factory</a>.</p>

<h2 id="Example">Example</h2>

<p>To send a SQL request to a database, a database library provides the following class:</p>

<pre><code class="language-ruby">class Request
  def initialize(driver)
    @driver = driver
  end
  
  def execute(sql_query)
    begin
      @db_driver.open_connection
      result = @db_driver.send(sql_query)
    ensure
      @db_driver.close_connection
    end
  end
end</code></pre>

<p>This class uses a driver which is also provided by the library, and that handles the communication with a specific database management system. The driver class has the following methods: <code>open_connection</code>, <code>send</code> and <code>close_connection</code>, which is the interface that <code>Request</code> expects from its <code>driver</code> parameter.</p>

<p>There are several drivers classes. For example one driver class connects to SQLite databases:</p>

<pre><code class="language-ruby">class SQLiteDriver
  def open_connection
    # opens a connection to the SQLite database
  end
  
  def send(sql_query)
    # sends a SQL query to the database
  end
  
  def close_connection
    # closes the connection to the database
  end
end</code></pre>

<p>And another one connects to PostGreSQL:</p>

<pre><code class="language-ruby">class PostGreSQLDriver
  def open_connection
    # opens a connection to the PostGreSQL database
  end
  
  def send(sql_query)
    # sends a SQL query to the database
  end
  
  def close_connection
    # closes the connection to the database
  end
end</code></pre>

<p>And so on.</p>

<p>To send a request to a particular database system, for example SQLite, we can write the following code:</p>

<pre><code class="language-ruby">sqlite_request = Request.new(SQLiteDriver.new)
sqlite_request.execute("select count(*) from users")</code></pre>

<p>The issue with this class is that lots of code in an application use the same database management system, and it would be more convenient and less error prone to have one class that represents a request for a specific system, for example an SQLite request.</p>

<p>How can we plug together the <code>Request</code> class with the <code>SQLiteDriver</code> class to create such a <code>SQLiteRequest</code> class?</p>

<h2 id="Introducing-a-Factory-method">Introducing a Factory method</h2>

<p>One way to achieve that is to change the code of the <code>Request</code> class to introduce a customization point <code>create_db_driver</code> that creates a specific database driver instead of passing the driver at the initialization of the <code>Request</code>. This is the Factory method design pattern:</p>

<pre><code class="language-ruby">class Request
  def execute(sql_query)
    begin
      db_driver = create_db_driver # customization point here
      db_driver.open_connection
      result = db_driver.send(sql_query)
    ensure
      db_driver.close_connection
    end
  end
end</code></pre>

<p>The <code>Request</code> class expects a derived class to provide an implementation for the <code>create_db_driver</code> method:</p>

<pre><code class="language-ruby">class SQLiteRequest &lt; Request
  def create_db_driver
    SQLiteDriver.new
  end
end</code></pre>

<p>We can now create a request this way:</p>

<pre><code class="language-ruby">SQLiteRequest.new.execute("select count(*) from users")</code></pre>

<p>This calls the <code>execute</code> method on the <code>Request</code> base class, that calls the <code>create_db_driver</code> from the derived class <code>SQLiteRequest</code> to get a driver and use it in its generic code.</p>

<p>Similarly, to use PostGreSQL we can have a derived class that creates a PostGreSQL driver:</p>

<pre><code class="language-ruby">class PostGreSQLRequest &lt; Request
  def create_db_driver
    PostGreSQLDriver.new
  end
end</code></pre>

<p>An we can use that class to create a PostGreSQL request:<code>PostGreSQLRequest.new.execute("select count(*) from users")</code></p>

<p>That’s how Factory method is a special kind of a <a href="/template-method">Template method</a> where the customization point consists in creating an object of a certain type.</p>

<p>(In the particular example of database system, note that it’s a good idea not to let the name of the database system, nor even the fact that the application uses a database, spread in the codebase. This is the idea behind <a href="/hexagonal-architecture">Hexagonal architecture</a>)</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Factory method is like Template method with customization points that create objects</li><li>It uses Polymorphism to decouple the code that use an object from the one that creates it</li><li>Factory method allows generic code to be used with multiple types of objects</li></ul>
</div>`

export default content
